/**
 * RecentCallsList Component
 * Shows list of recently called queue numbers
 */

import React from 'react';
import { Box, Typography, List, ListItem, Paper } from '@mui/material';
import { DisplayCallData } from '../../../types/display';

interface RecentCallsListProps {
  calls: DisplayCallData[];
}

/**
 * Component to display list of recent queue calls
 */
export const RecentCallsList: React.FC<RecentCallsListProps> = ({ calls }) => {
  const formatQueueNumber = (num: number): string => {
    return num.toString().padStart(3, '0');
  };

  const formatTime = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Paper
      elevation={2}
      sx={{
        flex: 1,
        p: 3,
        borderRadius: 3,
        backgroundColor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Typography
        variant="h5"
        sx={{
          mb: 2,
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
          fontWeight: 600,
          color: 'text.secondary',
        }}
      >
        ULTIMAS CHAMADAS
      </Typography>

      {/* List */}
      {calls.length > 0 ? (
        <List disablePadding sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
          {calls.map((call, index) => (
            <ListItem
              key={`${call.queueNumber}-${index}`}
              sx={{
                py: 1.5,
                px: 0,
                borderBottom: index < calls.length - 1 ? 1 : 0,
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  gap: 2,
                }}
              >
                {/* Queue Number */}
                <Typography
                  sx={{
                    fontSize: 'clamp(24px, 4vw, 48px)',
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    color: index === 0 ? 'primary.main' : 'text.primary',
                    minWidth: '80px',
                  }}
                >
                  {formatQueueNumber(call.queueNumber)}
                </Typography>

                {/* Patient Name */}
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: 'clamp(16px, 2vw, 24px)',
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {call.employeeName}
                </Typography>

                {/* Time */}
                <Typography
                  sx={{
                    fontSize: 'clamp(14px, 1.5vw, 18px)',
                    color: 'text.disabled',
                    fontFamily: 'monospace',
                  }}
                >
                  {formatTime(call.calledAt)}
                </Typography>
              </Box>
            </ListItem>
          ))}
        </List>
      ) : (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.disabled">
            Nenhuma chamada recente
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default RecentCallsList;
